import { RefObject, useEffect, useState } from "react";
import { deferFn } from "../../../../common/utils/function.ts";
import { useFetchers, useSearchParams } from "react-router-dom";

// "deep links" reader return back to originating content search panel
// based off library_return_url passed to and back from supernova

export default function useReaderSearchReturn(
  containerRef?: RefObject<HTMLElement>
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnDetail, setReturnDetail] = useState<string>("");
  const fetchers = useFetchers();

  useEffect(() => {
    // when returning from reader want to show last open search content panel
    // need to remove param passed back from reader
    const detail = searchParams.get("library_return_detail");
    if (detail) {
      searchParams.delete("library_return_detail");
      setSearchParams(searchParams, {
        replace: true,
        preventScrollReset: true,
      });
      setReturnDetail(detail);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const containerElement = containerRef ? containerRef.current : document;

    if (
      containerElement &&
      returnDetail &&
      !fetchers.find((f) => f.state === "loading")
    ) {
      deferFn(() => {
        // deferred so focus gets into panel and buttons exist
        // also counteracts focus restoration which sets it to top

        // This button is a hidden offscreen search button on the card,
        // not the real search button in the three-dot
        const btn = containerElement.querySelector(
          `[data-library-return='${returnDetail}']`
        ) as HTMLElement | undefined;
        if (btn) {
          // go back to previous search user had open (keeping section and scroll pos)
          // not supposed to focus something aria hidden, so temp enable it, will switch immediately
          btn.setAttribute("aria-hidden", "false");
          btn.focus(); // for prettyScrollToOpener
          btn.click();
          btn.addEventListener(
            "focus",
            () => {
              btn.setAttribute("aria-hidden", "true");
              // don't want focus to go back to hidden helper, want to go to three dot
              const focusTarget = btn
                .closest("li")
                ?.querySelector("[data-return-focus-destination]") as
                | HTMLElement
                | undefined;
              if (focusTarget) {
                focusTarget.focus();
              }
            },
            { once: true }
          );
        }
        setReturnDetail("");
      });
    }
  }, [containerRef, fetchers, returnDetail]);
}
